/**
 * Rows for the "Hardware Reference: Fulfilled by Contract Manufacturer" table shown for
 * software-subscription-only vendors (e.g. Supermicro, Dell), where hardware
 * is quoted by the partner. Shared by the quote PDF and the on-screen BOM
 * view so both always show the same data.
 *
 * Ported from FORK/src/utils/hardwareReference.js. Adapted for this repo's
 * metrics shape: the non-suffixed capacity fields (totalAfaSsdCapacity,
 * totalHybridSsdCapacity, totalHddCapacity, ...) are display-unit-converted —
 * they scale by the user's Binary/Decimal (isPBUnit) toggle (see
 * src/metrics/capacityUnitInvariance.test.js). The `...CapacityTB` fields are
 * the invariant raw-TB totals, which is what both the SSD/HDD device-count
 * division and the "Total Raw Capacity (TB)" column need regardless of the
 * user's display-unit preference. Director capacity
 * (`totalVeloSsdCapacity`) has no TB-suffixed counterpart because it is never
 * unit-converted in the first place (see src/metrics/calculators/directorMetrics.ts).
 */
import { resolveFlashNodeModel } from './flashNodeModel';
export const buildHardwareReferenceRows = (config, metrics, vendorDisplayName) => {
  const rows = [];

  // Director nodes (always) - Storage Device Qty = SSDs per single director node
  const totalDirectorSsds = config.veloSsdCapacity > 0
    ? Math.round(metrics.totalVeloSsdCapacity / config.veloSsdCapacity)
    : 0;
  const ssdPerVelo = config.veloCount > 0 ? Math.round(totalDirectorSsds / config.veloCount) : 0;
  rows.push({
    nodeDescription: `${vendorDisplayName} Director Nodes`,
    nodeQty: config.veloCount,
    deviceCapacityTB: config.veloSsdCapacity,
    deviceQty: ssdPerVelo,
    totalRawTB: metrics.totalVeloSsdCapacity
  });

  // All Flash nodes (if AFA enabled) - Storage Device Qty = SSDs per single AFA node
  if (config.afaEnabled && metrics.totalAfaSsdCapacityTB > 0) {
    const totalAfaSsds = config.afaVpodSsdCapacity > 0
      ? Math.round(metrics.totalAfaSsdCapacityTB / config.afaVpodSsdCapacity)
      : 0;
    const ssdPerAfaNode = config.afaNodeCount > 0 ? Math.round(totalAfaSsds / config.afaNodeCount) : 0;
    // CONF-210: say which flash node is being quoted.
    const flashModel = resolveFlashNodeModel(config);
    rows.push({
      nodeDescription: `${vendorDisplayName} ${flashModel.id === 'dualCpu' ? 'Dual CPU ' : ''}All Flash Nodes`,
      nodeQty: config.afaNodeCount,
      deviceCapacityTB: config.afaVpodSsdCapacity,
      deviceQty: ssdPerAfaNode,
      totalRawTB: metrics.totalAfaSsdCapacityTB
    });
  }

  // Hybrid Storage nodes + JBODs (if hybrid enabled) - Storage Device Qty = SSDs per single storage node
  if (config.hybridEnabled && metrics.totalHybridSsdCapacityTB > 0) {
    const totalHybridSsds = config.hybridVpodSsdCapacity > 0
      ? Math.round(metrics.totalHybridSsdCapacityTB / config.hybridVpodSsdCapacity)
      : 0;
    const ssdPerHybridNode = config.hybridNodeCount > 0 ? Math.round(totalHybridSsds / config.hybridNodeCount) : 0;
    rows.push({
      nodeDescription: `${vendorDisplayName} Storage Nodes`,
      nodeQty: config.hybridNodeCount,
      deviceCapacityTB: config.hybridVpodSsdCapacity,
      deviceQty: ssdPerHybridNode,
      totalRawTB: metrics.totalHybridSsdCapacityTB
    });
  }

  if (config.hybridEnabled && metrics.totalHddCapacityTB > 0) {
    // Storage Device Qty for JBOD = drives per single JBOD unit = jbodSize
    rows.push({
      nodeDescription: `${vendorDisplayName} JBODs`,
      nodeQty: config.hybridNodeCount,
      deviceCapacityTB: config.hybridHddCapacity,
      deviceQty: config.jbodSize,
      totalRawTB: metrics.totalHddCapacityTB
    });
  }

  return rows;
};
